import React from 'react';
import { BookOpen, Download, ExternalLink, Star, Edit } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../lib/auth';
import { Resource } from '../lib/types';

const ResourcesPanel: React.FC = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const isAdmin = user?.role === 'admin';

  const resources: Resource[] = [
    {
      id: '1',
      title: 'Guide complet pour rédiger des offres d\'emploi efficaces',
      description: 'Découvrez les meilleures pratiques pour créer des offres d\'emploi qui attirent les meilleurs talents.',
      type: 'PDF',
      rating: 4.8,
      downloads: 1245,
      imageUrl: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      introduction: 'Dans ce guide complet, nous allons explorer les techniques essentielles pour rédiger des offres d\'emploi qui attirent les meilleurs talents.',
      content: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: '2',
      title: 'Les erreurs à éviter dans vos annonces de recrutement',
      description: 'Identifiez et corrigez les erreurs courantes qui font fuir les candidats qualifiés.',
      type: 'Article',
      rating: 4.5,
      downloads: 876,
      imageUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      introduction: 'Découvrez les erreurs les plus courantes dans la rédaction d\'offres d\'emploi et apprenez à les éviter.',
      content: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: '3',
      title: 'Modèles d\'offres d\'emploi par secteur',
      description: 'Une collection de modèles personnalisables pour différents secteurs d\'activité.',
      type: 'Templates',
      rating: 4.9,
      downloads: 2389,
      imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      introduction: 'Explorez notre collection de modèles d\'offres d\'emploi adaptés à différents secteurs d\'activité.',
      content: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: '4',
      title: 'Webinaire : Optimiser vos offres pour les plateformes de recrutement',
      description: 'Apprenez à optimiser vos offres pour LinkedIn, Indeed et autres plateformes populaires.',
      type: 'Vidéo',
      rating: 4.7,
      downloads: 654,
      imageUrl: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      introduction: 'Un webinaire complet sur l\'optimisation de vos offres d\'emploi pour les principales plateformes de recrutement.',
      content: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ];

  const handleResourceClick = (resourceId: string) => {
    navigate(`/resources/${resourceId}`);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Ressources</h2>
      
      <p className="text-gray-600 mb-8">
        Explorez nos ressources pour améliorer vos compétences en rédaction d'offres d'emploi et optimiser votre processus de recrutement.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {resources.map(resource => (
          <div 
            key={resource.id} 
            className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 flex flex-col h-full cursor-pointer transform transition-transform hover:scale-105"
            onClick={() => handleResourceClick(resource.id)}
          >
            <div className="h-40 overflow-hidden">
              <img 
                src={resource.imageUrl} 
                alt={resource.title} 
                className="w-full h-full object-cover transition-transform hover:scale-110"
              />
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold text-gray-800">{resource.title}</h3>
                <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full">
                  {resource.type}
                </span>
              </div>
              <p className="text-gray-600 mb-4 flex-1">{resource.description}</p>
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <Star className="w-4 h-4 text-yellow-500 mr-1" />
                  <span className="text-sm text-gray-700">{resource.rating}/5</span>
                  <span className="text-xs text-gray-500 ml-2">({resource.downloads} téléchargements)</span>
                </div>
                <div className="flex space-x-2">
                  <button className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm">
                    <Download className="w-4 h-4 mr-1" />
                    <span>Télécharger</span>
                  </button>
                  <button className="text-gray-600 hover:text-gray-800 flex items-center text-sm">
                    {isAdmin ? (
                      <>
                        <Edit className="w-4 h-4 mr-1" />
                        <span>Modifier</span>
                      </>
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-1" />
                        <span>Voir</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResourcesPanel;